import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import React from "react";
import { Link } from "react-router-dom";

interface Props {
  id: string;
  type: string;
  title: string;
  body: string;
  createdAt: string;
  isRead: boolean;
  link?: string;
  level?: string;
  alertLevel?: string;
  onMarkAsRead?: (id: string) => void;
  isMarkingRead?: boolean;
}

const NotificationCard: React.FC<Props> = ({
  id,
  type,
  title,
  body,
  createdAt,
  isRead,
  link,
  level,
  alertLevel,
  onMarkAsRead,
  isMarkingRead = false,
}) => {
  const { palette } = useTheme();
  const formattedDate = new Date(createdAt).toLocaleString();
  const normalizedLevel = String(alertLevel || level || type || "")
    .trim()
    .toLowerCase();

  const colorByLevel = () => {
    if (
      normalizedLevel.includes("critical") ||
      normalizedLevel.includes("error")
    )
      return palette.error.main;
    if (
      normalizedLevel.includes("high") ||
      normalizedLevel.includes("warning") ||
      normalizedLevel.includes("warn")
    )
      return palette.warning.main;
    if (normalizedLevel.includes("success")) return palette.success.main;
    if (normalizedLevel.includes("low") || normalizedLevel.includes("info"))
      return palette.info.main;
    return palette.secondary.main;
  };

  const levelColor = colorByLevel();

  const handleMarkAsRead = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onMarkAsRead?.(id);
  };

  return (
    <Box
      sx={{
        position: "relative",
        borderBottom: `1px solid ${alpha(palette.secondary.main, 0.1)}`,
        borderLeft: `3px solid ${levelColor}`,
        backgroundColor: alpha(levelColor, isRead ? 0.03 : 0.08),

        "& .notification-mark-read": {
          opacity: isRead ? 1 : 0,
          pointerEvents: isRead ? "auto" : "none",
          transition: "opacity 0.15s ease",
        },

        "&:hover .notification-mark-read, &:focus-within .notification-mark-read":
          {
            opacity: 1,
            pointerEvents: "auto",
          },
      }}
    >
      <Button
        onClick={() => {
          if (!isRead) {
            onMarkAsRead?.(id);
          }
        }}
        sx={{
          width: "100%",
          position: "relative",
          px: "10px",
          py: "12px",
          textAlign: "left",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          borderRadius: 0,
          textTransform: "none",
          backgroundColor: "transparent",
          color: palette.text.secondary,

          "&:hover, &:focus": {
            backgroundColor: alpha(levelColor, 0.1),
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            pr: 4.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography fontSize={11} color={palette.text.secondary}>
              {formattedDate}
            </Typography>
          </Box>

          <Typography
            mt={0.5}
            fontSize={14}
            fontWeight={600}
            color={palette.common.black}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>

          <Typography
            mt={0.25}
            fontSize={12}
            color={palette.text.secondary}
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {body}
          </Typography>
        </Box>

        {link ? (
          <Link
            to={link}
            style={{
              position: "absolute",
              inset: 0,
              display: "block",
            }}
          />
        ) : null}
      </Button>

      <IconButton
        size="small"
        onClick={handleMarkAsRead}
        disabled={isRead || isMarkingRead}
        className="notification-mark-read"
        sx={{
          position: "absolute",
          right: 8,
          top: "50%",
          transform: "translateY(-50%)",
          backgroundColor: "transparent",
          border: "none",
          color: isRead ? palette.success.main : palette.text.secondary,

          "&:hover": {
            backgroundColor: alpha(palette.secondary.main, 0.06),
          },

          "&.Mui-disabled": {
            color: palette.success.main,
            opacity: 0.8,
          },
        }}
      >
        {isRead ? (
          <DoneAllIcon fontSize="small" />
        ) : (
          <CheckCircleOutlineIcon fontSize="small" />
        )}
      </IconButton>
    </Box>
  );
};

export default NotificationCard;
